import React from "react";

/** Animated skeleton block — relies on global `.serveiq-shimmer` in serveiq-shimmer.css */
export const ShimmerBox = ({ className = "", style = {}, ...props }) => (
  <div className={`serveiq-shimmer ${className}`.trim()} style={style} {...props} />
);

/** Menu product list skeleton (horizontal cards like ProductCard menu-row). */
export function MenuGridShimmer({ rows = 4 } = {}) {
  return (
    <div className="serveiq-shimmer-menu-grid" aria-hidden>
      <ShimmerBox className="serveiq-shimmer-menu-grid__heading" />
      <div className="serveiq-shimmer-menu-grid__list">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="serveiq-shimmer-menu-row">
            <div className="serveiq-shimmer-menu-row__copy">
              <ShimmerBox className="serveiq-shimmer-menu-row__title" />
              <ShimmerBox className="serveiq-shimmer-menu-row__price" />
              <ShimmerBox className="serveiq-shimmer-menu-row__desc" />
            </div>
            <ShimmerBox className="serveiq-shimmer-menu-row__thumb" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Single mobile menu product skeleton card. */
export function MobileMenuProductShimmerCard() {
  return (
    <div className="serveiq-shimmer-mobile-product-card" aria-hidden>
      <div className="serveiq-shimmer-mobile-product-card__body">
        <ShimmerBox className="serveiq-shimmer-mobile-product-card__title" />
        <ShimmerBox className="serveiq-shimmer-mobile-product-card__desc" />
        <ShimmerBox className="serveiq-shimmer-mobile-product-card__desc serveiq-shimmer-mobile-product-card__desc--short" />
        <div className="serveiq-shimmer-mobile-product-card__footer">
          <ShimmerBox className="serveiq-shimmer-mobile-product-card__price" />
          <ShimmerBox className="serveiq-shimmer-mobile-product-card__btn" />
        </div>
      </div>
      <ShimmerBox className="serveiq-shimmer-mobile-product-card__thumb" />
    </div>
  );
}

/** Mobile / tablet menu list — mirrors `.mobile-product-card` layout. */
export function MobileMenuProductsShimmer({ rows = 4, asGridItems = false } = {}) {
  const cards = Array.from({ length: rows }, (_, i) => (
    <MobileMenuProductShimmerCard key={i} />
  ));

  if (asGridItems) {
    return <>{cards}</>;
  }

  return (
    <div
      className="serveiq-shimmer-mobile-products"
      role="status"
      aria-live="polite"
      aria-label="Loading menu items"
    >
      <div className="serveiq-shimmer-mobile-products__list">{cards}</div>
    </div>
  );
}

/** Skeleton for GridSectionLoader + similar large content regions (Home, menu grid, lists). */
export function GridSectionShimmer() {
  return (
    <div className="serveiq-shimmer-grid-panel" aria-hidden>
      <ShimmerBox style={{ height: 18, width: "38%", marginBottom: 14, borderRadius: 8 }} />
      <div className="serveiq-shimmer-grid-cards">
        <ShimmerBox style={{ height: 118, borderRadius: 12 }} />
        <ShimmerBox style={{ height: 118, borderRadius: 12 }} />
        <ShimmerBox style={{ height: 118, borderRadius: 12 }} />
        <ShimmerBox style={{ height: 118, borderRadius: 12 }} />
      </div>
      <div className="serveiq-shimmer-stack" style={{ marginTop: 14 }}>
        <ShimmerBox style={{ height: 12, width: "88%", borderRadius: 6 }} />
        <ShimmerBox style={{ height: 12, width: "72%", borderRadius: 6 }} />
        <ShimmerBox style={{ height: 12, width: "56%", borderRadius: 6 }} />
      </div>
    </div>
  );
}

/** Full-viewport initial load (Preloader variant="fullPage"). */
export function FullPageShimmer() {
  return (
    <div className="serveiq-shimmer-full-page" aria-hidden>
      <ShimmerBox className="serveiq-shimmer-full-page__hero" />
      <div className="serveiq-shimmer-stack serveiq-shimmer-full-page__rows">
        <ShimmerBox style={{ height: 14, width: "72%", borderRadius: 6 }} />
        <ShimmerBox style={{ height: 14, width: "88%", borderRadius: 6 }} />
        <ShimmerBox style={{ height: 14, width: "52%", borderRadius: 6 }} />
      </div>
      <div className="serveiq-shimmer-grid-cards">
        <ShimmerBox style={{ height: 96, borderRadius: 12 }} />
        <ShimmerBox style={{ height: 96, borderRadius: 12 }} />
      </div>
    </div>
  );
}

/** React.lazy route Suspense fallback — whole-app chunk load. */
export function RouteFallbackShimmer() {
  return (
    <div className="serveiq-shimmer-route serveiq-shimmer-stack" aria-hidden>
      <ShimmerBox style={{ height: 28, width: "36%", borderRadius: 8 }} />
      <ShimmerBox style={{ height: 14, width: "100%", borderRadius: 6 }} />
      <ShimmerBox style={{ height: 14, width: "92%", borderRadius: 6 }} />
      <ShimmerBox style={{ height: 14, width: "68%", borderRadius: 6 }} />
      <ShimmerBox style={{ height: 120, width: "100%", borderRadius: 12, marginTop: 8 }} />
    </div>
  );
}
